import networkTS from '@/misc/apis/networkTS'
import { normalizeTypeDate } from '../utils/normalizeTypeDate'
import type { Contact } from '@/types/models'
import type {
  ContactServiceAddContactPersonPayload,
  ContactServiceUpdateCompanyPayload,
  ContactServiceUpdateContactPersonPayload,
  ContactServiceUpdatePersonPayload,
  Contactcontactv2Type,
  IndustryIndustry,
  V2CreateCompanyRequest,
  V2CreatePersonRequest
} from '@/types/api'

type ContactWithApiFields = Contact & {
  types?: Array<string | { value?: string }>
  industries?: Array<string | { value?: string }>
  phone?: {
    home1?: string | null
    home2?: string | null
    work?: string | null
    mobile?: string | null
    fax?: string | null
  }
  language?: string
  dod?: string | Date
  dob?: string | Date
  companyName?: string
  companyPosition?: string
}

function prepareTypes(contact: ContactWithApiFields) {
  if (!contact.types || contact.types.length === 0) {
    return ['NONE' as Contactcontactv2Type]
  } else if ((contact.types[0] as { value?: string } | undefined)?.value !== undefined) {
    return contact.types.map(type => typeof type === 'string' ? type : (type as { value?: string }).value) as Contactcontactv2Type[]
  } else {
    return contact.types as Contactcontactv2Type[]
  }
}
function prepareIndustries(contact: ContactWithApiFields) {
  if (!contact.industries || contact.industries.length === 0) {
    return ['INDUSTRY_UNDEFINED_VALUE' as IndustryIndustry]
  } else if ((contact.industries[0] as { value?: string } | undefined)?.value !== undefined) {
    return contact.industries.map(industrie => typeof industrie === 'string' ? industrie : (industrie as { value?: string }).value) as IndustryIndustry[]
  } else {
    return contact.industries as IndustryIndustry[]
  }
}

function preparePhoneNumbers(contact: ContactWithApiFields) {
  const phoneNumbers = []
  if (contact.phone && contact.phone.home1 !== null && contact.phone.home1 !== '') {
    phoneNumbers.push({
      number: contact.phone.home1,
      type: 'HOME'
    })
  }
  if (contact.phone && contact.phone.home2 !== null && contact.phone.home2 !== '') {
    phoneNumbers.push({
      number: contact.phone.home2,
      type: 'HOME'
    })
  }
  if (contact.phone && contact.phone.work !== null && contact.phone.work !== '') {
    phoneNumbers.push({
      number: contact.phone.work,
      type: 'WORK'
    })
  }
  if (contact.phone && contact.phone.mobile !== null && contact.phone.mobile !== '') {
    phoneNumbers.push({
      number: contact.phone.mobile,
      type: 'MOBILE'
    })
  }
  if (contact.phone && contact.phone.fax !== null && contact.phone.fax !== '') {
    phoneNumbers.push({
      number: contact.phone.fax,
      type: 'FAX'
    })
  }

  return phoneNumbers
}

export default {
  getContacts(accountId) {
    return networkTS.contactServiceList(accountId)
      .then(response => {
        return response.data
      })
  },
  async listEmployees(accountId) {
    return networkTS.contactServiceListEmployees(accountId)
      .then(response => {
        return response.data
      })
  },
  publishUpdatedContactEvents(ownerIds) {
    return networkTS.contactServicePublishUpdatedEvents({
      ownerIds: ownerIds
    })
      .then(response => {
        return response.data
      })
  },
  checkDuplicates(emails, firstName, isCompany, name, ownerId, address, phoneNumbers) {
    return networkTS.contactServiceCheckDuplicates({
      emails: emails,
      firstName: firstName,
      ownerId: ownerId,
      isCompany: isCompany,
      name: name,
      address: address,
      phoneNumbers: phoneNumbers
    })
      .then(response => {
        return response.data
      })
  },
  copyContacts(sourceAccountId, targetTargetId) {
    return networkTS.contactServiceCopyContacts({
      sourceAccountId: sourceAccountId,
      targetTargetId: targetTargetId
    })
      .then(response => {
        return response.data
      })
  },
  duplicateContacts(contactIds: string[], targetOwnerId: string) {
    return networkTS.contactServiceDuplicateContacts({
      ids: contactIds,
      targetOwnerId: targetOwnerId
    })
      .then(response => {
        return response.data
      })
  },
  async contactResolve(contactIds: string[]) {
    return networkTS.contactServiceResolve({ contactIds: contactIds })
      .then(response => {
        return response.data
      })
  },
  employeeResolve(ownerId, emails) {
    return networkTS.contactServiceResolveEmployees({
      ownerId: ownerId,
      emails: emails
    })
      .then(response => {
        return response.data
      })
  },
  resolveByMail(ownerId, emails) {
    return networkTS.contactServiceResolveByMail(ownerId, {
      emails: emails
    })
      .then(response => {
        return response.data
      })
  },
  getOwnerContact(accountId) {
    return networkTS.contactServiceGetOwnerContact(accountId)
      .then(response => {
        return response.data
      })
  },
  linkPersons(incomingContactId, outgoingContactId) {
    return networkTS.contactServiceLinkPersons({
      relations: [{
        incomingContactId: incomingContactId,
        outgoingContactId: outgoingContactId
      }]
    })
      .then(response => {
        return response.data
      })
  },
  unLinkPersons(incomingContactId, outgoingContactId) {
    return networkTS.contactServiceUnLinkPersons({
      relations: [{
        incomingContactId: incomingContactId,
        outgoingContactId: outgoingContactId
      }]
    })
      .then(response => {
        return response.data
      })
  },
  linkCompanies(incomingContactId, outgoingContactId) {
    return networkTS.contactServiceLinkCompanies({
      relations: [{
        incomingContactId: incomingContactId,
        outgoingContactId: outgoingContactId
      }]
    })
      .then(response => {
        return response.data
      })
  },
  unLinkCompanies(incomingContactId, outgoingContactId) {
    return networkTS.contactServiceUnLinkCompanies({
      relations: [{
        incomingContactId: incomingContactId,
        outgoingContactId: outgoingContactId
      }]
    })
      .then(response => {
        return response.data
      })
  },
  addContact(newContact: ContactWithApiFields, accountId, contactType) {
    const types = prepareTypes(newContact)
    const industries = prepareIndustries(newContact)
    const phoneNumbers = preparePhoneNumbers(newContact)

    if (contactType === 'company') {
      const payload = {
        ownerId: accountId,
        types: types,
        email: newContact.email,
        name: newContact.name,
        note: newContact.note,
        address: newContact.address,
        industries: industries,
        phoneNumbers: phoneNumbers,
        documentIds: [],
        website: newContact.website,
        vatNumber: newContact.vatNumber,
        preferredCommunicationChannel: newContact.preferredCommunicationChannel,
        salutationName: newContact.salutationName,
        tags: newContact.tags,
        preferredLanguage: newContact.preferredLanguage,
        includeInSerialEmails: newContact.includeInSerialEmails,
        portalAccess: newContact.portalAccess,
        legalForm: newContact.legalForm,
        uniqueEnterpriseIdentification: newContact.uniqueEnterpriseIdentification === 'CHE-' ? '' : newContact.uniqueEnterpriseIdentification
      } as V2CreateCompanyRequest
      return networkTS.contactServiceCreateCompany(payload).then(response => {
        return response.data
      })
    } else {
      const payload = {
        ownerId: accountId,
        types: types,
        email: newContact.email,
        title: newContact.title,
        note: newContact.note,
        gender: newContact.gender,
        firstName: newContact.firstName,
        lastName: newContact.lastName,
        birthName: newContact.birthName,
        employment: newContact.employment,
        industries: industries,
        address: newContact.address,
        ahv: newContact.ahv,
        phoneNumbers: phoneNumbers,
        dateOfBirth: normalizeTypeDate(newContact.dateOfBirth),
        dateOfDeath: normalizeTypeDate(newContact.dateOfDeath),
        website: newContact.website,
        birthplace: newContact.birthplace,
        civilStatus: newContact.civilStatus,
        nationality: newContact.nationality,
        residenceStatus: newContact.residenceStatus,
        preferredCommunicationChannel: newContact.preferredCommunicationChannel,
        salutationName: newContact.salutationName,
        tags: newContact.tags,
        residenceStatusValidUntil: normalizeTypeDate(newContact.residenceStatusValidUntil),
        preferredLanguage: newContact.preferredLanguage,
        workEmail: newContact.workEmail
      } as V2CreatePersonRequest
      return networkTS.contactServiceCreatePerson(payload).then(response => {
        return response.data
      })
    }
  },
  editContact(contact: ContactWithApiFields) {
    const types = prepareTypes(contact)
    const phoneNumbers = preparePhoneNumbers(contact)
    const industries = prepareIndustries(contact)

    if (contact.contactPersons) {
      const payload: ContactServiceUpdateCompanyPayload = {
        ownerId: contact.ownerId,
        types: types,
        email: contact.email,
        note: contact.note,
        name: contact.name,
        address: contact.address,
        industries: industries,
        phoneNumbers: phoneNumbers,
        website: contact.website,
        preferredCommunicationChannel: contact.preferredCommunicationChannel,
        salutationName: contact.salutationName,
        tags: contact.tags,
        vatNumber: contact.vatNumber,
        preferredLanguage: contact.preferredLanguage,
        includeInSerialEmails: contact.includeInSerialEmails,
        portalAccess: contact.portalAccess,
        legalForm: contact.legalForm,
        uniqueEnterpriseIdentification: contact.uniqueEnterpriseIdentification === 'CHE-' ? '' : contact.uniqueEnterpriseIdentification
      }
      return networkTS.contactServiceUpdateCompany(contact.id, payload).then(response => {
        return response.data
      })
    } else {
      const payload: ContactServiceUpdatePersonPayload = {
        ownerId: contact.ownerId,
        types: types,
        email: contact.email,
        title: contact.title,
        note: contact.note,
        gender: contact.gender,
        firstName: contact.firstName,
        lastName: contact.lastName,
        name: contact.name,
        birthName: contact.birthName,
        employment: contact.employment,
        industries: industries,
        address: contact.address,
        phoneNumbers: phoneNumbers,
        dateOfDeath: normalizeTypeDate(contact.dateOfDeath),
        dateOfBirth: normalizeTypeDate(contact.dateOfBirth),
        ahv: contact.ahv,
        website: contact.website,
        birthplace: contact.birthplace,
        civilStatus: contact.civilStatus,
        nationality: contact.nationality,
        salutationName: contact.salutationName,
        residenceStatus: contact.residenceStatus,
        preferredCommunicationChannel: contact.preferredCommunicationChannel,
        tags: contact.tags,
        residenceStatusValidUntil: normalizeTypeDate(contact.residenceStatusValidUntil),
        preferredLanguage: contact.preferredLanguage,
        workEmail: contact.workEmail
      }
      return networkTS.contactServiceUpdatePerson(contact.id, payload).then(response => {
        return response.data
      })
    }
  },
  addContactPerson(newContact: ContactWithApiFields, companyContactId) {
    const phoneNumber = []
    if (newContact.phone.home1 !== null && newContact.phone.home1 !== '') {
      phoneNumber.push({
        number: newContact.phone.home1,
        type: 'HOME'
      })
    }
    if (newContact.phone.home2 !== null && newContact.phone.home2 !== '') {
      phoneNumber.push({
        number: newContact.phone.home2,
        type: 'HOME'
      })
    }
    if (newContact.phone.work !== null && newContact.phone.work !== '') {
      phoneNumber.push({
        number: newContact.phone.work,
        type: 'WORK'
      })
    }
    if (newContact.phone.mobile !== null && newContact.phone.mobile !== '') {
      phoneNumber.push({
        number: newContact.phone.mobile,
        type: 'MOBILE'
      })
    }
    if (newContact.phone.fax !== null && newContact.phone.fax !== '') {
      phoneNumber.push({
        number: newContact.phone.fax,
        type: 'FAX'
      })
    }
    const dod = new Date(newContact.dod)
    const dateOfDeath = dod instanceof Date && Number.isFinite(dod.getTime())
? {
      day: dod.getDate(),
      month: dod.getMonth() + 1,
      year: dod.getFullYear()
    }
: null
    const dob = new Date(newContact.dob)
    const dateOfBirth = dob instanceof Date && Number.isFinite(dob.getTime())
? {
      day: dob.getDate(),
      month: dob.getMonth() + 1,
      year: dob.getFullYear()
    }
: null
    const payload: ContactServiceAddContactPersonPayload = {
      email: newContact.email,
      gender: newContact.gender,
      title: newContact.title,
      note: newContact.note,
      firstName: newContact.firstName,
      lastName: newContact.lastName,
      birthName: newContact.birthName,
      employment: {
        companyName: newContact.companyName,
        companyPosition: newContact.companyPosition
      },
      address: newContact.address,
      phoneNumbers: phoneNumber,
      dateOfDeath: normalizeTypeDate(dateOfDeath),
      dateOfBirth: normalizeTypeDate(dateOfBirth),
      preferredLanguage: newContact.preferredLanguage,
      includeInSerialEmails: newContact.includeInSerialEmails,
      portalAccess: newContact.portalAccess
    }
    return networkTS.contactServiceAddContactPerson(companyContactId, payload).then(response => {
      return response.data
    })
  },
  editContactPerson(newContact: ContactWithApiFields, companyContactId, contactPersonId) {
    const phoneNumber = []
    if (newContact.phone) {
      if (newContact.phone.home1 !== null && newContact.phone.home1 !== '') {
        phoneNumber.push({
          number: newContact.phone.home1,
          type: 'HOME'
        })
      }
      if (newContact.phone.home2 !== null && newContact.phone.home2 !== '') {
        phoneNumber.push({
          number: newContact.phone.home2,
          type: 'HOME'
        })
      }
      if (newContact.phone.work !== null && newContact.phone.work !== '') {
        phoneNumber.push({
          number: newContact.phone.work,
          type: 'WORK'
        })
      }
      if (newContact.phone.mobile !== null && newContact.phone.mobile !== '') {
        phoneNumber.push({
          number: newContact.phone.mobile,
          type: 'MOBILE'
        })
      }
      if (newContact.phone.fax !== null && newContact.phone.fax !== '') {
        phoneNumber.push({
          number: newContact.phone.fax,
          type: 'FAX'
        })
      }
    }
    const dod = new Date(newContact.dod)
    const dateOfDeath = dod instanceof Date && Number.isFinite(dod.getTime())
? {
      day: dod.getDate(),
      month: dod.getMonth() + 1,
      year: dod.getFullYear()
    }
: null
    const dob = new Date(newContact.dob)
    const dateOfBirth = dob instanceof Date && Number.isFinite(dob.getTime())
? {
      day: dob.getDate(),
      month: dob.getMonth() + 1,
      year: dob.getFullYear()
    }
: null
    const payload: ContactServiceUpdateContactPersonPayload = {
      email: newContact.email,
      title: newContact.title,
      note: newContact.note,
      gender: newContact.gender,
      firstName: newContact.firstName,
      lastName: newContact.lastName,
      birthName: newContact.birthName,
      employment: {
        companyName: newContact.companyName,
        companyPosition: newContact.companyPosition
      },
      address: newContact.address,
      phoneNumbers: phoneNumber,
      dateOfDeath: normalizeTypeDate(dateOfDeath),
      dateOfBirth: normalizeTypeDate(dateOfBirth),
      preferredLanguage: newContact.preferredLanguage,
      salutationName: newContact.salutationName,
      includeInSerialEmails: newContact.includeInSerialEmails,
      portalAccess: newContact.portalAccess
    }
    return networkTS.contactServiceUpdateContactPerson(companyContactId, contactPersonId, payload).then(response => {
      return response.data
    })
  },
  deleteContactPerson(companyContactId, contactPersonIds) {
    return networkTS.contactServiceDeleteContactPersons(companyContactId, {
      contactPersonIds: contactPersonIds
    }).then(response => {
      return response.data
    })
  },
  activateContact(contactIds) {
    return networkTS.contactServiceActivate({
      ids: contactIds
    }).then(response => {
      return response.data
    })
  },
  deactivateContact(contactIds) {
    return networkTS.contactServiceDeactivate({
      ids: contactIds
    }).then(response => {
      return response.data
    })
  },
  addDocuments(id, documentIds) {
    return networkTS.contactServiceAddDocuments(id, {
      documentIds: documentIds
    }).then(response => {
      return response.data
    })
  },
  deleteDocuments(id, documentIds) {
    return networkTS.contactServiceRemoveDocuments(id, documentIds).then(response => {
      return response.data
    })
  },
  addActivity(activity, referenceId) {
    return networkTS.activityServiceCreateMixin5({
      contactIds: activity.contactIds,
      referenceIds: referenceId,
      employeeContactId: activity.employeeContactId ? activity.employeeContactId : null,
      subject: activity.subject,
      text: activity.text,
      timestamp: activity.timestamp ? String(Math.round(activity.timestamp.getTime() / 1000)) : null,
      documentIds: activity.documentIds
    }).then(response => {
      return response.data
    })
  },
  addActivityV3(activity) {
    return networkTS.activityServiceCreate({
      ownerId: activity.ownerId,
      references: activity.references,
      employeeContactIds: activity.employeeContactIds,
      subject: activity.subject,
      text: activity.text,
      source: activity.source,
      timestampStart: activity.timestampStart,
      timestampEnd: activity.timestampEnd,
      mediaIds: activity.mediaIds,
      hasStartTime: activity.hasStartTime,
      hasEndTime: activity.hasEndTime,
      billable: activity.billable || false,
      hourlyRate: activity.hourlyRate || null
    }).then(response => {
      return response.data
    })
  },
  updateActivityV3(activity) {
    return networkTS.activityServiceUpdate(activity.id, {
      references: activity.references,
      employeeContactIds: activity.employeeContactIds,
      subject: activity.subject,
      text: activity.text,
      source: activity.source,
      timestampStart: activity.timestampStart,
      timestampEnd: activity.timestampEnd,
      mediaIds: activity.mediaIds,
      hasStartTime: activity.hasStartTime,
      hasEndTime: activity.hasEndTime,
      billable: activity.billable || false,
      hourlyRate: activity.hourlyRate || null
    }).then(response => {
      return response.data
    })
  },
  async getActivitiesByReferenceIdsV3(referenceIds: string[]) {
    return networkTS.activityServiceListByReferenceIds({ referenceIds: referenceIds })
      .then(response => {
        return response.data
      })
  },
  setAddress(address, ids, validFrom) {
    return networkTS.contactServiceSetAddress({
      address: address,
      ids: ids,
      validFrom: validFrom
    }).then(response => {
      return response.data
    })
  },
  updateAddressHistory(address, ids, oldValidFrom, validFrom) {
    return networkTS.contactServiceUpdateAddressHistory({
      address: address,
      ids: ids,
      oldValidFrom: oldValidFrom,
      validFrom: validFrom
    }).then(response => {
      return response.data
    })
  },
  getUsedTags(accountId) {
    return networkTS.contactServiceGetUsedTags(accountId)
      .then(response => {
        return response.data
      })
  },
  deleteAddress(id, validFrom) {
    return networkTS.contactServiceDeleteAddress(id, {
      validFrom: validFrom
    }).then(response => {
      return response.data
    })
  },
  deleteActivity(id) {
    return networkTS.activityServiceDelete(id).then(response => {
      return response.data
    })
  }
}
